import { useState, useEffect, useCallback } from 'react';

// Detect Safari browser
const isSafari = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  const webkit = !!ua.match(/WebKit/i);
  const iOSSafari = iOS && webkit && !ua.match(/CriOS/i) && !ua.match(/FxiOS/i);
  const macSafari = ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');
  return iOSSafari || macSafari;
};

// Detect iOS
const isIOS = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
};

// Check if installed
const checkIsInstalled = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

export function useInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  
  // Initialize these synchronously to avoid cascading renders
  const [isSafariBrowser] = useState(() => isSafari());
  const [isIOSDevice] = useState(() => isIOS());
  const [isInstalled, setIsInstalled] = useState(() => checkIsInstalled());

  useEffect(() => {
    // Skip if already installed
    if (isInstalled) return;

    // Listen for the beforeinstallprompt event (Chromium browsers only)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return false;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstallable(false);
      setInstallPrompt(null);
      return true;
    }
    return false;
  }, [installPrompt]);

  return { 
    isInstallable, 
    isInstalled, 
    promptInstall, 
    isSafari: isSafariBrowser, 
    isIOS: isIOSDevice 
  };
}
